import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Vietnamese mirror of the power-of-attorney guide. */
const TITLE = "Giấy uỷ quyền Thái Lan dùng ở nước ngoài — viết thế nào để không bị trả lại";
const DESC =
  "Cách soạn giấy uỷ quyền Thái Lan để dùng ở nước ngoài: ghi phạm vi uỷ quyền, thứ tự công chứng và chứng nhận, cùng những lỗi khiến phải làm lại.";
const PATH = "/blog/power-of-attorney-abroad-guide";
const URL = `/vi${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "Giấy uỷ quyền cần có những nội dung gì?",
    answer:
      "Tên và số giấy tờ của người uỷ quyền và người được uỷ quyền, phạm vi công việc được làm, đối tượng tài sản hoặc hồ sơ liên quan, thời hạn hiệu lực, và chữ ký kèm ngày ký.",
  },
  {
    question: "Có cần công chứng Notary không?",
    answer:
      "Nếu dùng ở nước ngoài thì thường cần. Giấy uỷ quyền là giấy tờ tư nhân, nên phải có Notarial Services Attorney xác nhận chữ ký trước khi đi tiếp các bước chứng nhận.",
  },
  {
    question: "Thứ tự đúng là gì?",
    answer:
      "Ký trước mặt luật sư công chứng → chứng nhận tại Cục Lãnh sự Bộ Ngoại giao khi được yêu cầu → dịch → hợp pháp hoá tại đại sứ quán nước tiếp nhận nếu cần.",
  },
  {
    question: "Viết phạm vi rộng cho tiện có được không?",
    answer:
      "Phạm vi quá rộng dễ bị từ chối, phạm vi quá hẹp thì không làm được việc. Hãy nêu rõ từng hành vi được phép, ví dụ xin, nhận, ký nhận và nộp hồ sơ.",
  },
  {
    question: "Giấy uỷ quyền có thời hạn không?",
    answer:
      "Có thể ghi thời hạn hoặc để hiệu lực đến khi hoàn thành công việc. Nhiều cơ quan tiếp nhận chỉ nhận bản mới trong vòng 3–6 tháng.",
  },
  {
    question: "Ký ở nước ngoài thì làm thế nào?",
    answer:
      "Thường ký tại đại sứ quán hoặc tổng lãnh sự quán Thái Lan, hoặc công chứng theo pháp luật nước đó rồi chứng nhận thêm để Thái Lan chấp nhận.",
  },
  {
    question: "Vì sao giấy uỷ quyền bị trả lại?",
    answer:
      "Phổ biến nhất là phạm vi không đủ rõ, thiếu số giấy tờ định danh, dịch trước khi chứng nhận, và tên viết không khớp hộ chiếu.",
  },
];

export const Route = createFileRoute("/vi/blog/power-of-attorney-abroad-guide")({
  component: ViPoaGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "vi" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("power-of-attorney-abroad-guide") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("power-of-attorney-abroad-guide", "vi") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("power-of-attorney-abroad-guide") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "giấy uỷ quyền Thái Lan, power of attorney Thái Lan, công chứng Notary Thái Lan, chứng nhận giấy uỷ quyền",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "vi"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "Trang chủ", url: "/vi" },
              { name: "Hướng dẫn", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("power-of-attorney-abroad-guide"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Notary Public",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const ELEMENTS: Array<{ d: string; note: string }> = [
  { d: "Thông tin người uỷ quyền và người được uỷ quyền", note: "Tên đúng như hộ chiếu kèm số giấy tờ định danh" },
  { d: "Phạm vi công việc", note: "Liệt kê từng hành vi: xin, nhận, ký nhận, nộp, rút hồ sơ" },
  { d: "Đối tượng liên quan", note: "Số hồ sơ, số thửa đất, tên công ty hoặc loại giấy tờ cụ thể" },
  { d: "Thời hạn hiệu lực", note: "Ghi ngày kết thúc hoặc gắn với việc hoàn thành công việc" },
  { d: "Chữ ký và ngày ký", note: "Ký trước mặt luật sư công chứng, không ký sẵn từ trước" },
  { d: "Ngôn ngữ và bản dịch", note: "Dịch sau khi đã hoàn tất các lớp chứng nhận" },
];

const ORDER: string[] = [
  "Xác nhận với cơ quan tiếp nhận về mẫu và phạm vi họ yêu cầu",
  "Soạn giấy uỷ quyền với phạm vi rõ ràng, đủ nhưng không quá rộng",
  "Ký trước mặt Notarial Services Attorney",
  "Chứng nhận tại Cục Lãnh sự Bộ Ngoại giao khi được yêu cầu",
  "Dịch toàn văn kèm con dấu, tem và chú thích",
  "Hợp pháp hoá tại đại sứ quán nước tiếp nhận nếu cần, rồi rà soát lần cuối",
];

const MISTAKES: string[] = [
  "Phạm vi uỷ quyền chung chung nên bị từ chối tại quầy",
  "Thiếu số giấy tờ định danh của một trong hai bên",
  "Ký sẵn trước khi gặp luật sư công chứng",
  "Dịch trước khi hoàn tất chứng nhận",
  "Tên viết không khớp hộ chiếu hoặc giấy tờ gốc",
  "Không ghi thời hạn nên cơ quan tiếp nhận coi là không rõ ràng",
];

function ViPoaGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/vi" className="text-muted-foreground hover:text-foreground">
          Trang chủ
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          Hướng dẫn
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="power-of-attorney-abroad-guide" locale="vi" />
      <p className="answer-summary speakable-summary">
        Giấy uỷ quyền Thái Lan bị trả lại thường không vì thiếu chữ ký, mà vì phạm vi uỷ quyền viết không đủ rõ và vì làm
        sai thứ tự. Hãy nêu từng hành vi được phép, ký trước mặt luật sư công chứng, chứng nhận tại Cục Lãnh sự, rồi mới
        dịch và hợp pháp hoá tại đại sứ quán nếu cần.
      </p>

      <h2>1. Nội dung bắt buộc nên có</h2>
      <ul>
        {ELEMENTS.map((x) => (
          <li key={x.d}>
            <strong>{x.d}:</strong> {x.note}
          </li>
        ))}
      </ul>

      <h2>2. Thứ tự thực hiện</h2>
      <ol>
        {ORDER.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. Lỗi khiến phải làm lại</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. Câu hỏi thường gặp</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. Nguồn tham khảo và dịch vụ liên quan</h2>
      <p className="text-sm">
        Tham khảo:{" "}
        <a href="https://www.lawyerscouncil.or.th/" target="_blank" rel="noreferrer">
          Liên đoàn Luật sư Thái Lan
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          Cục Lãnh sự, Bộ Ngoại giao Thái Lan
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/vi/notary" className="text-primary underline">
            Dịch vụ công chứng Notary bởi luật sư
          </Link>
        </li>
        <li>
          <Link to="/vi/lgz" className="text-primary underline">
            Chứng nhận và hợp pháp hoá giấy tờ
          </Link>
        </li>
        <li>
          <Link to="/vi/pcc" className="text-primary underline">
            Lý lịch tư pháp và giấy tờ hộ tịch
          </Link>
        </li>

      </ul>
      <p className="text-sm text-muted-foreground">
        Nội dung rà soát ngày {PUBLISHED}. Đây là thông tin chung, không phải ý kiến pháp lý cho vụ việc cụ thể. Nếu bạn
        không muốn tự theo từng bước, đội ngũ hơn 15 năm kinh nghiệm của chúng tôi có thể tư vấn nội dung và thực hiện
        toàn bộ — liên hệ qua điện thoại, LINE hoặc email.
      </p>
    </article>
  );
}
